import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const POLL_MAX = 6;

export const DonateSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setInfo(data); setState("paid"); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return; }
      } catch (e) { if (process.env.NODE_ENV === "development") console.debug("donation status poll retry", e); }
      attempts += 1;
      if (attempts >= POLL_MAX) { setState("timeout"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="donate-success">
      <Card className="max-w-lg w-full">
        <CardContent className="p-10 text-center">
          {state === "checking" && (
            <>
              <Loader2 className="w-12 h-12 text-amber-600 animate-spin mx-auto mb-6" />
              <h1 className="text-xl font-bold text-slate-900">Confirming your donation...</h1>
            </>
          )}
          {state === "paid" && (
            <>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-9 h-9 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="donate-thankyou">Thank You for Your Generosity!</h1>
              <p className="text-slate-600 mb-2">Your {info?.frequency === "monthly" ? "monthly" : "one-time"} donation of <strong>${info?.amount}</strong>{info?.frequency === "monthly" ? "/month" : ""} was received.</p>
              <p className="text-slate-500 text-sm mb-6">Soar Your Visibility–Dress to Impress is a nonprofit serving the local community. Through our fiscal sponsorship, all donations are tax deductible. You'll receive a brief email receipt confirming your donation{info?.frequency === "monthly" ? " each month" : ""}. If you need a detailed tax receipt, just let us know.</p>
              <Link to="/"><Button variant="outline" className="gap-2" data-testid="donate-home">Back to Home <ArrowRight className="w-4 h-4" /></Button></Link>
            </>
          )}
          {(state === "timeout" || state === "error") && (
            <>
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-9 h-9 text-amber-600" />
              </div>
              <h1 className="text-xl font-bold text-slate-900 mb-3">We couldn't confirm your donation yet</h1>
              <p className="text-slate-600 mb-6">If you completed payment, it may take a moment to process. Please check your email for a receipt, or contact us if you have questions.</p>
              <Link to="/"><Button variant="outline" className="gap-2">Back to Home <ArrowRight className="w-4 h-4" /></Button></Link>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export const DonateCancel = () => (
  <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="donate-cancel">
    <Card className="max-w-lg w-full">
      <CardContent className="p-10 text-center">
        <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <XCircle className="w-9 h-9 text-slate-500" />
        </div>
        <h1 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>Donation Cancelled</h1>
        <p className="text-slate-600 mb-6">No charge was made. We'd love your support whenever you're ready.</p>
        <div className="flex gap-3 justify-center">
          <Link to="/donate"><Button className="bg-amber-600 hover:bg-amber-700 text-white">Try Again</Button></Link>
          <Link to="/"><Button variant="outline">Back to Home</Button></Link>
        </div>
      </CardContent>
    </Card>
  </div>
);
